import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddOutlined as AddIcon,
  DeleteOutline as DeleteIcon,
  DownloadOutlined as DownloadIcon,
  EditOutlined as EditIcon,
  FileUploadOutlined as ImportIcon,
  IosShareOutlined as ExportIcon,
  SearchOutlined as SearchIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../apiBase';

const initialRows = [];

function downloadTextFile({ filename, content, mime = 'text/plain;charset=utf-8' }) {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function toCsvValue(v) {
  const s = String(v ?? '');
  if (s.includes(',') || s.includes('"') || s.includes('\n')) {
    return `"${s.replaceAll('"', '""')}"`;
  }
  return s;
}

function parseCsvText(text) {
  // Minimal CSV parser good enough for simple templates (no multiline cells).
  // Returns array of rows, each row is string[].
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const rows = [];
  for (const line of lines) {
    const cells = [];
    let cur = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
      const ch = line[i];
      if (ch === '"') {
        const next = line[i + 1];
        if (inQuotes && next === '"') {
          cur += '"';
          i += 1;
        } else {
          inQuotes = !inQuotes;
        }
        continue;
      }
      if (ch === ',' && !inQuotes) {
        cells.push(cur.trim());
        cur = '';
        continue;
      }
      cur += ch;
    }
    cells.push(cur.trim());
    rows.push(cells);
  }
  return rows;
}

function normalizeHeaderCell(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/_/g, '');
}

function looksLikeExcelFile(file) {
  const name = String(file?.name || '').toLowerCase();
  return name.endsWith('.xlsx') || name.endsWith('.xls');
}

async function parseExcelFileToRows(file) {
  // Lazy-load to keep initial bundle smaller
  const XLSX = await import('xlsx');
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames?.[0];
  const ws = wb.Sheets?.[sheetName];
  if (!ws) return [];

  // header:1 => array-of-arrays like CSV parser output
  const aoa = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
  return Array.isArray(aoa) ? aoa : [];
}

export default function ArticleCreation() {
  const fileInputRef = useRef(null);

  const { user } = useAuth();
  const role = user?.role || '';
  const isSuperAdmin = role === 'superadmin';
  const permissionsMap = user?.page_permissions_map || {};
  const pagePerm =
    permissionsMap.article_creation ||
    permissionsMap.articles ||
    permissionsMap.articlecreation ||
    {};
  const canCreate = isSuperAdmin || Boolean(pagePerm.can_create);
  const canUpdate = isSuperAdmin || Boolean(pagePerm.can_update);
  const canDelete = isSuperAdmin || Boolean(pagePerm.can_delete);
  const canImport = isSuperAdmin || Boolean(pagePerm.can_import);
  const canExport = isSuperAdmin || Boolean(pagePerm.can_export);

  const [rows, setRows] = useState(() => initialRows);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const [orderBy, setOrderBy] = useState('srNo'); // 'srNo' | 'item' | 'gstRatio'
  const [order, setOrder] = useState('desc'); // 'asc' | 'desc'
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingRowId, setEditingRowId] = useState(null);
  const [formItem, setFormItem] = useState('');
  const [formGstRatio, setFormGstRatio] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteRowId, setDeleteRowId] = useState(null);

  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const loadArticles = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/articles`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Failed to load articles');
      setRows(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Failed to load articles', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadArticles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filteredRows = useMemo(() => {
    const q = String(query || '').trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const item = String(r.item || '').toLowerCase();
      const sr = String(r.srNo ?? '').toLowerCase();
      const gst = String(r.gstRatio ?? '').toLowerCase();
      return item.includes(q) || sr.includes(q) || gst.includes(q);
    });
  }, [rows, query]);

  const sortedRows = useMemo(() => {
    const stabilized = filteredRows.map((r, idx) => [r, idx]);
    const cmp = (a, b) => {
      const ra = a[0];
      const rb = b[0];
      const dir = order === 'asc' ? 1 : -1;
      if (orderBy === 'srNo' || orderBy === 'gstRatio') {
        const va = Number(ra[orderBy] || 0);
        const vb = Number(rb[orderBy] || 0);
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return a[1] - b[1];
      }
      const va = String(ra[orderBy] || '').toLowerCase();
      const vb = String(rb[orderBy] || '').toLowerCase();
      if (va < vb) return -1 * dir;
      if (va > vb) return 1 * dir;
      return a[1] - b[1];
    };
    stabilized.sort(cmp);
    return stabilized.map((x) => x[0]);
  }, [filteredRows, order, orderBy]);

  const pagedRows = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedRows.slice(start, start + rowsPerPage);
  }, [page, rowsPerPage, sortedRows]);

  const handleSort = (col) => {
    if (orderBy === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setOrderBy(col);
    setOrder(col === 'item' ? 'asc' : 'desc');
  };

  const gstChipSx = (gst) => {
    const n = Number(gst);
    if (!Number.isFinite(n)) return { bgcolor: 'action.hover', color: 'text.primary', fontWeight: 800 };
    if (n <= 5) return { bgcolor: 'rgba(34,197,94,0.14)', color: '#16a34a', fontWeight: 800 };
    if (n <= 12) return { bgcolor: 'rgba(59,130,246,0.14)', color: '#2563eb', fontWeight: 800 };
    if (n <= 18) return { bgcolor: 'rgba(245,158,11,0.16)', color: '#b45309', fontWeight: 800 };
    return { bgcolor: 'rgba(239,68,68,0.14)', color: '#dc2626', fontWeight: 800 };
  };

  const formatGstDisplay = (value) => {
    const n = Number(value);
    if (!Number.isFinite(n)) return '';
    if (Number.isInteger(n)) return String(n);
    return n.toFixed(2);
  };

  const openAddDialog = () => {
    if (!canCreate) {
      setToast({
        open: true,
        message: 'You do not have permission to add articles',
        severity: 'error',
      });
      return;
    }
    setEditingRowId(null);
    setFormItem('');
    setFormGstRatio('');
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const openEditDialog = (row) => {
    if (!canUpdate) {
      setToast({
        open: true,
        message: 'You do not have permission to update articles',
        severity: 'error',
      });
      return;
    }
    setEditingRowId(row.id);
    setFormItem(row.item || '');
    setFormGstRatio(formatGstDisplay(row.gstRatio));
    setFormErrors({});
    setEditDialogOpen(true);
  };

  const validateForm = () => {
    const next = {};
    const item = formItem.trim();
    const gstRaw = String(formGstRatio).trim();
    const gst = gstRaw === '' ? NaN : Number(gstRaw);

    if (!item) next.item = 'Item is required';
    if (gstRaw === '') next.gstRatio = 'GST is required';
    if (gstRaw !== '' && !Number.isFinite(gst)) next.gstRatio = 'GST must be a number';
    if (Number.isFinite(gst) && gst < 0) next.gstRatio = 'GST must be zero or positive';

    const duplicate = rows.find(
      (r) => r.item?.trim().toLowerCase() === item.toLowerCase() && r.id !== editingRowId
    );
    if (duplicate) next.item = 'This item already exists';

    setFormErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleGstBlur = () => {
    const raw = String(formGstRatio).trim();
    if (!raw) return;
    const formatted = formatGstDisplay(raw);
    if (formatted) {
      setFormGstRatio(formatted);
    }
  };

  const saveDialog = () => {
    if (!validateForm()) return;
    const item = formItem.trim();
    const gstRatio = Number(formGstRatio);

    (async () => {
      setLoading(true);
      try {
        if (editingRowId) {
          if (!canUpdate) {
            setToast({
              open: true,
              message: 'You do not have permission to update articles',
              severity: 'error',
            });
            return;
          }
          const res = await fetch(`${API_BASE_URL}/api/articles/${editingRowId}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
            body: JSON.stringify({ item, gstRatio }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.error || 'Failed to update article');
          await loadArticles();
          setToast({ open: true, message: 'Article updated', severity: 'success' });
        } else {
          if (!canCreate) {
            setToast({
              open: true,
              message: 'You do not have permission to add articles',
              severity: 'error',
            });
            return;
          }
          const res = await fetch(`${API_BASE_URL}/api/articles`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
            },
            body: JSON.stringify({ item, gstRatio }),
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok) throw new Error(data?.error || 'Failed to add article');
          await loadArticles();
          setToast({ open: true, message: 'Article added', severity: 'success' });
        }
        setEditDialogOpen(false);
      } catch (e) {
        setToast({ open: true, message: e?.message || 'Action failed', severity: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  };

  const confirmDelete = (rowId) => {
    if (!canDelete) {
      setToast({
        open: true,
        message: 'You do not have permission to delete articles',
        severity: 'error',
      });
      return;
    }
    setDeleteRowId(rowId);
    setDeleteDialogOpen(true);
  };

  const doDelete = () => {
    if (!canDelete) {
      setToast({
        open: true,
        message: 'You do not have permission to delete articles',
        severity: 'error',
      });
      setDeleteDialogOpen(false);
      setDeleteRowId(null);
      return;
    }
    (async () => {
      const id = deleteRowId;
      if (!id) return;
      setLoading(true);
      try {
        const res = await fetch(`${API_BASE_URL}/api/articles/${id}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${localStorage.getItem('token') || ''}` },
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data?.error || 'Failed to delete article');
        setRows((prev) => prev.filter((r) => r.id !== id));
        setToast({ open: true, message: 'Article deleted', severity: 'info' });
      } catch (e) {
        setToast({ open: true, message: e?.message || 'Failed to delete', severity: 'error' });
      } finally {
        setLoading(false);
        setDeleteDialogOpen(false);
        setDeleteRowId(null);
      }
    })();
  };

  const exportCsv = () => {
    if (!canExport) {
      setToast({
        open: true,
        message: 'You do not have permission to export articles',
        severity: 'error',
      });
      return;
    }
    const header = ['Sr No', 'Item', 'Gst Ratio'];
    const body = [...rows]
      .sort((a, b) => Number(a.srNo || 0) - Number(b.srNo || 0))
      .map((r) => [r.srNo, r.item, r.gstRatio].map(toCsvValue).join(','))
      .join('\n');
    downloadTextFile({
      filename: 'articles.csv',
      content: `${header.join(',')}\n${body}\n`,
      mime: 'text/csv;charset=utf-8',
    });
  };

  const downloadTemplate = () => {
    const header = ['Item', 'Gst Ratio'];
    const example = ['Cement', 18].map(toCsvValue).join(',');
    downloadTextFile({
      filename: 'articles_template.csv',
      content: `${header.join(',')}\n${example}\n`,
      mime: 'text/csv;charset=utf-8',
    });
  };

  const triggerImport = () => {
    if (!canImport) {
      setToast({
        open: true,
        message: 'You do not have permission to import articles',
        severity: 'error',
      });
      return;
    }
    fileInputRef.current?.click?.();
  };

  const handleImportFile = async (file) => {
    if (!canImport) {
      setToast({
        open: true,
        message: 'You do not have permission to import articles',
        severity: 'error',
      });
      return;
    }
    if (!file) return;
    try {
      const parsed = looksLikeExcelFile(file)
        ? await parseExcelFileToRows(file)
        : parseCsvText(await file.text());
      if (parsed.length === 0) throw new Error('Empty file');

      const header = parsed[0].map(normalizeHeaderCell);
      const body = parsed.slice(1);

      // accept either: [item, gstratio] or [srno, item, gstratio]
      const idxItem = header.findIndex((h) => h === 'item');
      const idxGst = header.findIndex((h) => h === 'gstratio' || h === 'gstrate' || h === 'gstrate%');
      const idxSr = header.findIndex((h) => h === 'srno' || h === 'srnumber' || h === 'sr');

      if (idxItem === -1 || idxGst === -1) {
        throw new Error('CSV headers must include: Item, Gst Ratio');
      }

      const incoming = [];
      for (const row of body) {
        const item = String(row[idxItem] ?? '').trim();
        const gst = Number(String(row[idxGst] ?? '').trim());
        if (!item) continue;
        if (!Number.isFinite(gst)) continue;

        const srFromCsv = idxSr >= 0 ? Number(String(row[idxSr] ?? '').trim()) : null;
        incoming.push({ item, gstRatio: gst, srNo: Number.isFinite(srFromCsv) ? srFromCsv : null });
      }

      if (incoming.length === 0) throw new Error('No valid rows found in CSV');

      setLoading(true);
      const res = await fetch(`${API_BASE_URL}/api/articles/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
        },
        body: JSON.stringify({ items: incoming }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.error || 'Import failed');

      await loadArticles();
      setToast({
        open: true,
        message: `Import completed: ${data?.inserted ?? 0} added, ${data?.skipped ?? 0} skipped`,
        severity: 'success',
      });
    } catch (e) {
      setToast({ open: true, message: e?.message || 'Import failed', severity: 'error' });
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
      setLoading(false);
    }
  };

  return (
    <Layout title="Article Creation">
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'stretch', md: 'center' }}
          justifyContent="space-between"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 650, letterSpacing: -0.3 }}>
              Article Creation
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.75 }}>
              Manage items and GST ratios. Import/export via CSV.
            </Typography>
          </Box>

          <Stack direction="row" spacing={1.25} justifyContent="flex-end" flexWrap="wrap">
            <Button startIcon={<AddIcon />} variant="contained" onClick={openAddDialog} disabled={!canCreate}>
              Add
            </Button>
            <Button
              startIcon={<ExportIcon />}
              variant="contained"
              color="success"
              onClick={exportCsv}
              disabled={loading || !canExport}
            >
              Export
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              variant="contained"
              color="info"
              onClick={downloadTemplate}
              disabled={loading}
            >
              Download Template
            </Button>
            <Button
              startIcon={<ImportIcon />}
              variant="contained"
              color="inherit"
              onClick={triggerImport}
              sx={{ bgcolor: 'grey.700', color: 'common.white', '&:hover': { bgcolor: 'grey.800' } }}
              disabled={loading || !canImport}
            >
              Import
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv,.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              style={{ display: 'none' }}
              onChange={(e) => handleImportFile(e.target.files?.[0])}
            />
          </Stack>
        </Stack>

        <Paper
          variant="outlined"
          sx={{
            borderRadius: 4,
            overflow: 'hidden',
            borderColor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.08)'),
          }}
        >
          <Box
            sx={{
              px: 2,
              py: 1.5,
              bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#eef2ff'),
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
              spacing={1.25}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography sx={{ fontWeight: 750 }}>Articles</Typography>
                <Chip
                  size="small"
                  label={
                    rows.length === sortedRows.length
                      ? `${rows.length} total`
                      : `Showing ${sortedRows.length} of ${rows.length}`
                  }
                  sx={{ bgcolor: 'rgba(124,58,237,0.12)', color: 'secondary.main', fontWeight: 800 }}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
                <TextField
                  size="small"
                  placeholder="Search item / sr no / GST…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setPage(0);
                  }}
                  sx={{ minWidth: { xs: '100%', md: 320 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                {query.trim() && (
                  <Button
                    variant="text"
                    color="inherit"
                    onClick={() => {
                      setQuery('');
                      setPage(0);
                    }}
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    Clear
                  </Button>
                )}
              </Stack>
            </Stack>
          </Box>
          <Divider />

          <TableContainer>
            <Table stickyHeader sx={{ minWidth: 680 }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& .MuiTableCell-head': {
                      bgcolor: (t) =>
                        t.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : '#f1f5f9',
                      color: (t) => (t.palette.mode === 'dark' ? 'common.white' : '#0f172a'),
                      fontWeight: 800,
                      py: 1.5,
                    },
                  }}
                >
                  <TableCell sx={{ width: 180 }}>
                    <TableSortLabel
                      active={orderBy === 'srNo'}
                      direction={orderBy === 'srNo' ? order : 'asc'}
                      onClick={() => handleSort('srNo')}
                      sx={{
                        color: 'inherit',
                        '&:hover': { color: 'inherit' },
                        '&.Mui-active': { color: 'inherit' },
                        '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                      }}
                    >
                      Sr No
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'item'}
                      direction={orderBy === 'item' ? order : 'asc'}
                      onClick={() => handleSort('item')}
                      sx={{
                        color: 'inherit',
                        '&:hover': { color: 'inherit' },
                        '&.Mui-active': { color: 'inherit' },
                        '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                      }}
                    >
                      Item
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: 200 }}>
                    <TableSortLabel
                      active={orderBy === 'gstRatio'}
                      direction={orderBy === 'gstRatio' ? order : 'asc'}
                      onClick={() => handleSort('gstRatio')}
                      sx={{
                        color: 'inherit',
                        '&:hover': { color: 'inherit' },
                        '&.Mui-active': { color: 'inherit' },
                        '& .MuiTableSortLabel-icon': { color: 'inherit !important' },
                      }}
                    >
                      Gst Ratio
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="center" sx={{ width: 200, bgcolor: '#0b60ff' }}>
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {pagedRows.map((r) => (
                  <TableRow
                    key={r.id}
                    hover
                    sx={{
                      '& td': { py: 2.25, fontSize: 15 },
                      '&:hover td': { bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : '#f8fafc') },
                    }}
                  >
                    <TableCell>{r.srNo}</TableCell>
                    <TableCell sx={{ fontWeight: 650 }}>{r.item}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={formatGstDisplay(r.gstRatio)}
                        sx={{
                          ...gstChipSx(r.gstRatio),
                          borderRadius: 2,
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.75} justifyContent="center">
                        <Tooltip title="Edit" arrow>
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(r)}
                            disabled={loading || !canUpdate}
                            sx={{
                              borderRadius: 2,
                              color: '#0b60ff',
                              bgcolor: 'rgba(11,96,255,0.08)',
                              '&:hover': { bgcolor: 'rgba(11,96,255,0.14)' },
                            }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete" arrow>
                          <IconButton
                            size="small"
                            onClick={() => confirmDelete(r.id)}
                            disabled={loading || !canDelete}
                            sx={{
                              borderRadius: 2,
                              color: 'error.main',
                              bgcolor: 'rgba(239,68,68,0.10)',
                              '&:hover': { bgcolor: 'rgba(239,68,68,0.16)' },
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}

                {pagedRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 750 }}>
                          {query.trim() ? 'No matching articles' : 'No articles yet'}
                        </Typography>
                        <Typography sx={{ color: 'text.secondary', mt: 0.75 }}>
                          {query.trim()
                            ? 'Try a different search or clear the filter.'
                            : 'Create your first article or import a CSV template.'}
                        </Typography>
                        <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                          {query.trim() ? (
                            <Button
                              variant="outlined"
                              onClick={() => {
                                setQuery('');
                                setPage(0);
                              }}
                            >
                              Clear search
                            </Button>
                          ) : (
                            <Button variant="contained" startIcon={<AddIcon />} onClick={openAddDialog}>
                              Add Article
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />
          <TablePagination
            component="div"
            count={sortedRows.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[5, 10, 25]}
          />
        </Paper>
      </Box>

      {/* Add/Edit dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 800 }}>
          {editingRowId ? 'Edit Article' : 'Add Article'}
        </DialogTitle>
        <DialogContent>
          <Stack
            spacing={2}
            sx={{ pt: 1 }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                saveDialog();
              }
            }}
          >
            <TextField
              label="Item"
              value={formItem}
              onChange={(e) => setFormItem(e.target.value)}
              error={Boolean(formErrors.item)}
              helperText={formErrors.item || ' '}
              autoFocus
            />
            <TextField
              label="GST Ratio"
              value={formGstRatio}
              onChange={(e) => setFormGstRatio(e.target.value)}
              onBlur={handleGstBlur}
              error={Boolean(formErrors.gstRatio)}
              helperText={formErrors.gstRatio || 'Must be zero or positive'}
              inputProps={{ inputMode: 'decimal' }}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditDialogOpen(false)} color="inherit">
          Cancel
          </Button>
          <Button
            onClick={saveDialog}
            variant="contained"
            disabled={
              loading ||
              (Boolean(editingRowId) && !canUpdate) ||
              (!editingRowId && !canCreate)
            }
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>Delete Article?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary' }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={doDelete} variant="contained" color="error" disabled={loading || !canDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={2500}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Layout>
  );
}


